<template>
  <div class="health-assessment-report">
    <div v-if="isLoading" class="state-wrap">
      <van-loading type="spinner" color="#44a2df" vertical>正在获取健康评估报告...</van-loading>
    </div>

    <div v-else-if="!reportData" class="state-wrap">
      <van-empty description="暂无健康评估报告" />
    </div>

    <div v-else class="report-stack">
      <CoverPage :report="reportData" :brand-name="brandName" />
      <CatalogPage :report="reportData" />
      <ReportFrame
        v-if="showReportContent"
        :brand-name="brandName"
        :report-date="reportDate"
        :page-number="3"
        @content-action="handleReportContentAction"
      >
        <FourDiagnosis
          v-if="showFourDiagnosis"
          :report="reportData"
          :chapter-title="chapterTitle(0)"
        />
        <RiskAssessment
          v-if="showRiskAssessment"
          :report="reportData"
          :chapter-title="chapterTitle(1)"
        />
        <RegulatePlan
          v-if="showRegulatePlan"
          :report="reportData"
          :chapter-title="chapterTitle(2)"
          @content-action="handleReportContentAction"
        />
      </ReportFrame>
    </div>
  </div>
</template>

<script>
import { Empty, Loading, Toast } from 'vant';
import { post } from '@/utils/request';
import { isMiniProgramEnvironment } from '@/utils/viewportZoom';
import CoverPage from './components/CoverPage';
import CatalogPage from './components/CatalogPage';
import ReportFrame from './components/ReportFrame';
import FourDiagnosis from './components/FourDiagnosis';
import RiskAssessment from './components/RiskAssessment';
import RegulatePlan from './components/RegulatePlan';
import { formatReportDate, getReportBrandName, hasValueDeep } from './helpers';

export default {
  name: 'HealthAssessmentReport',
  components: {
    [Loading.name]: Loading,
    [Empty.name]: Empty,
    CoverPage,
    CatalogPage,
    ReportFrame,
    FourDiagnosis,
    RiskAssessment,
    RegulatePlan,
  },
  data() {
    return {
      isLoading: true,
      reportId: '',
      reportData: null,
    };
  },
  computed: {
    brandName() {
      return getReportBrandName(this.reportData || {});
    },
    reportDate() {
      if (!this.reportData) return '';
      return formatReportDate(
        this.reportData.detectTime
          || this.reportData.createAt
          || this.reportData.createTime,
      );
    },
    showFourDiagnosis() {
      if (!this.reportData) return false;
      return hasValueDeep(this.reportData.healthAnalysis)
        || hasValueDeep(this.reportData.faceReport)
        || hasValueDeep(this.reportData.tongueReport);
    },
    showRiskAssessment() {
      return hasValueDeep(
        this.reportData
          && this.reportData.riskReport
          && this.reportData.riskReport.riskList,
      );
    },
    showRegulatePlan() {
      return hasValueDeep(this.reportData && this.reportData.regulateReport);
    },
    showReportContent() {
      return this.showFourDiagnosis || this.showRiskAssessment || this.showRegulatePlan;
    },
  },
  created() {
    const reportId = this.getReportId();
    if (!reportId) {
      this.isLoading = false;
      Toast({ message: '缺少报告ID', position: 'middle' });
      return;
    }

    this.reportId = reportId;
    this.fetchReportData(reportId);
  },
  methods: {
    handleReportContentAction(action) {
      if (action === 'hyoxy-buy') this.goToGoodsUrl();
    },
    goToGoodsUrl() {
      const regulateReport = this.reportData && this.reportData.regulateReport;
      const hyoxy = regulateReport && regulateReport.hyoxy;
      const url = hyoxy && hyoxy.goodsUrl;
      if (!url) {
        Toast({ message: '商品链接无效', position: 'middle' });
        return;
      }

      const urlStr = String(url).trim();
      if (!urlStr) {
        Toast({ message: '商品链接无效', position: 'middle' });
        return;
      }

      if (/^https?:\/\//i.test(urlStr)) {
        window.location.href = urlStr;
        return;
      }

      this.openMiniProgramPage(urlStr);
    },
    openMiniProgramPage(url) {
      const miniProgram = window.wx && window.wx.miniProgram;
      const userAgent = navigator.userAgent || '';
      const hasMiniProgramSignal = isMiniProgramEnvironment(userAgent);
      if (!miniProgram || (!hasMiniProgramSignal && !/MicroMessenger/i.test(userAgent))) {
        Toast({ message: '请在微信小程序中打开', position: 'middle' });
        return;
      }

      this.resolveMiniProgramEnvironment(miniProgram, userAgent).then((isMiniProgram) => {
        if (!isMiniProgram) {
          Toast({ message: '请在微信小程序中打开', position: 'middle' });
          return;
        }
        this.navigateToMiniProgramPage(miniProgram, url);
      });
    },
    resolveMiniProgramEnvironment(miniProgram, userAgent) {
      if (isMiniProgramEnvironment(userAgent)) return Promise.resolve(true);

      return new Promise((resolve) => {
        let settled = false;
        let timer = null;
        const finish = (result) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(result);
        };
        timer = setTimeout(() => finish(false), 2500);

        try {
          miniProgram.getEnv((result) => {
            finish(Boolean(result && result.miniprogram));
          });
        } catch (error) {
          finish(false);
        }
      });
    },
    navigateToMiniProgramPage(miniProgram, url) {
      const showFailure = (error) => {
        const message = error && error.errMsg ? error.errMsg : '未知错误';
        Toast({ message: ['跳转失败：', message].join(''), position: 'middle', duration: 5000 });
      };
      const redirectToPage = (navigateError) => {
        if (typeof miniProgram.redirectTo !== 'function') {
          showFailure(navigateError);
          return;
        }
        miniProgram.redirectTo({
          url,
          fail: showFailure,
        });
      };

      try {
        miniProgram.navigateTo({
          url,
          fail: redirectToPage,
        });
      } catch (error) {
        redirectToPage(error);
      }
    },
    chapterTitle(index) {
      const outline = this.reportData && this.reportData.outline;
      if (!outline || !Array.isArray(outline) || !outline[index]) return '';
      const item = outline[index];
      const text = (item.zh || '') + (item.title || '');
      return text.replace('、', ' ');
    },
    getReportId() {
      const queryReportId = this.$route.query.reportId || this.$route.query.id;
      if (queryReportId) return queryReportId;

      const payloadStr = sessionStorage.getItem('MY_AI_SUBMIT_PAYLOAD')
        || localStorage.getItem('MY_AI_SUBMIT_PAYLOAD');
      if (!payloadStr) return '';

      try {
        const payload = JSON.parse(payloadStr);
        return payload.reportId || '';
      } catch (error) {
        console.error('解析缓存报告ID失败', error);
        return '';
      }
    },
    fetchReportData(reportId) {
      this.isLoading = true;
      post({
        url: '/healthy/open/consultationIpad/report',
        data: { reportId },
        options: {
          showLoading: false,
        },
      })
        .then((res) => {
          if (res instanceof Error) {
            Toast({ message: '获取健康评估报告失败', position: 'middle' });
            this.reportData = null;
            return;
          }

          if (res && res.code !== undefined && res.code !== 0) {
            Toast({ message: res.msg || '获取健康评估报告失败', position: 'middle' });
            this.reportData = null;
            return;
          }

          const result = res && res.data ? res.data : res;
          if (!result || typeof result !== 'object' || Array.isArray(result)) {
            Toast({ message: '健康评估报告数据异常', position: 'middle' });
            this.reportData = null;
            return;
          }
          this.reportData = result;
        })
        .catch(() => {
          Toast({ message: '获取健康评估报告失败', position: 'middle' });
          this.reportData = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.health-assessment-report {
  min-height: 100vh;
  background: #fff;
  color: #68311d;
  font-family: "Source Han Sans SC", "Source Han Sans CN", "Noto Sans CJK SC",
    "PingFang SC", "Microsoft YaHei", sans-serif;
}

.state-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 32px 16px;
  background: #f7f8fa;
}

.report-stack {
  width: 100%;
  max-width: none;
  margin: 0 auto;
  background: #fff;
}

.report-stack > * + * {
  margin-top: 4px;
  border-top: 0;
}

@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }

  .health-assessment-report {
    min-height: auto;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .report-stack {
    width: 210mm;
    max-width: none;
    margin: 0;
    background: #fff;
  }

  .report-stack > * {
    width: 210mm;
    box-sizing: border-box;
    margin: 0;
  }

  .report-stack > * + * {
    margin-top: 0;
  }
}
</style>

<style lang="scss">
.report-chapter + .report-chapter {
  margin-top: 12px;
}

.health-panel,
.text-pair,
.analysis-item,
.plan-item,
.statement,
.risk-item-row,
.risk-summary,
.risk-detail-head,
.scale-wrap,
.ribbon-row,
.person-row,
.photo-row,
.blue-note,
.food-advice {
  break-inside: avoid;
  page-break-inside: avoid;
}

.report-text-block--keep-together {
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  vertical-align: top;
  break-inside: avoid-column;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
}

.report-chapter-title,
.report-ribbon,
.ribbon-row,
.sub-title,
.device-group-title,
.interpret-title {
  break-after: avoid;
  page-break-after: avoid;
}

.risk-summary,
.item-list,
.blue-note,
.paragraphs,
.food-advice {
  break-before: avoid;
  page-break-before: avoid;
}

p {
  orphans: 3;
  widows: 3;
}

@media print {
  html,
  body,
  #app {
    width: 210mm;
    min-height: auto;
    margin: 0;
    padding: 0;
    background: #fff;
  }

  .cover-page,
  .catalog-page,
  .report-frame {
    width: 210mm !important;
    height: 297mm !important;
    min-height: 0 !important;
    aspect-ratio: auto !important;
    overflow: hidden !important;
    break-inside: avoid;
    page-break-inside: avoid;
    break-after: page;
    page-break-after: always;
  }

  .report-frame-list {
    width: 210mm !important;
  }

  .report-stack > :last-child.report-frame-list > .report-frame:last-child,
  .report-stack > .cover-page:last-child,
  .report-stack > .catalog-page:last-child {
    break-after: auto;
    page-break-after: auto;
  }

  #__vconsole {
    display: none !important;
  }
}
</style>
